import React, {useEffect} from 'react';
import Box from '@mui/material/Container';
import LinearProgress from '@mui/material/LinearProgress';
import {useDispatch, useSelector} from 'react-redux';
import {useMsal, useIsAuthenticated} from '@azure/msal-react';
import {InteractionStatus} from '@azure/msal-browser';
import {Outlet} from 'react-router-dom';
import {setAuthSuccess, setAuthToken, setLoading} from '../store/auth/authSlice';
import {IUserProfile} from 'app/types/Shared';
import {useMatomo} from '@jonkoops/matomo-tracker-react';
import config from 'config';
import {extractOrganizationFromToken} from 'app/helpers/auth';
import {
  LogInAction,
  selectUserLogInStatus,
  selectUserType,
  setLogInStatus,
  setUserType,
} from 'app/store/users/userSlice';

interface IdTokenClaims {
  email?: string;
  preferred_username?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  groups?: string[];
  idp?: string; // present for B2B guest (external/CRO) users; absent for internal Takeda members
  [key: string]: unknown;
}

export const RequiredAuth: React.FC = () => {
  const dispatch = useDispatch();
  const {pushInstruction} = useMatomo();

  const {instance, accounts, inProgress} = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const userLogInStatus = useSelector(selectUserLogInStatus);
  const userType = useSelector(selectUserType);
  const [loaded, setLoaded] = React.useState(false);

  useEffect(() => {
    const getRedirectStates = () => {
      if (!userType || !userLogInStatus) {
        return false;
      }
      if (!userLogInStatus.hasRedirected) {
        return true;
      }
      if (userLogInStatus.hasRedirected && !userLogInStatus.isLoggedIn) {
        return false;
      }
      if (userLogInStatus.hasRedirected && userLogInStatus.isLoggedIn) {
        return true;
      }
    };

    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (loaded) {
      return;
    }

    // use MSAL redirect to authenticate user if not logged in
    if (!isAuthenticated && getRedirectStates()) {
      dispatch(setLogInStatus(LogInAction.EnableRedirected));
      instance
        .loginRedirect({
          scopes: config.entra.scopes,
        })
        .then(() => {
          console.log('Redirect complete');
        })
        .catch((e) => console.log(e));
    } else if (!isAuthenticated && !getRedirectStates()) {
      console.log('Reset to inital Login state');
      dispatch(setUserType(undefined));
      dispatch(setLogInStatus(LogInAction.DisableAll));
    }

    // update store if user is authenticated
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];

      // Get ID token (with OIDC scopes only, no Graph scopes)
      instance
        .acquireTokenSilent({
          scopes: config.entra.scopes,
          account: account,
        })
        .then((response) => {
          // Use ID token claims directly (no Graph API call needed)
          const idTokenClaims = response.idTokenClaims as IdTokenClaims;
          console.log(
            '[SecureRoute] MSAL response - accessToken:',
            !!response.accessToken,
            'idToken:',
            !!response.idToken,
          );
          console.log('[SecureRoute] Response object keys:', Object.keys(response));

          const user: IUserProfile = {
            email: idTokenClaims?.email || idTokenClaims?.preferred_username || account.username || '',
            displayName: idTokenClaims?.name || account.name || '',
            firstName: idTokenClaims?.given_name || account.name?.split(' ')[0] || '',
            lastName: idTokenClaims?.family_name || account.name?.split(' ').slice(1).join(' ') || '',
            timeZone: '', // Not available in ID token
            groups: idTokenClaims?.groups || [], // Will be included if configured in Azure AD
          };
          dispatch(setAuthSuccess(user));
          dispatch(setLoading(false));

          // Store token in format compatible with existing code
          const tokenData = {
            accessToken: response.accessToken,
            idToken: response.idToken,
            expiresAt: response.expiresOn ? Math.floor(response.expiresOn.getTime() / 1000) : 0,
          };
          console.log('[SecureRoute] Dispatching tokenData:', tokenData);
          dispatch(setAuthToken(tokenData));

          // Determine Internal vs External based on Entra group membership.
          // Users in REACT_APP_ENTRA_INTERNAL_GROUP_IDS are Internal (Takeda).
          // Users not in those groups are External (CRO).
          console.log('[SecureRoute] CALLING extractOrganizationFromToken with idTokenClaims:', idTokenClaims);
          const detectedUserType = extractOrganizationFromToken(idTokenClaims as Record<string, unknown>);
          console.log('[SecureRoute] detectedUserType returned:', detectedUserType, '| current userType:', userType);
          if (detectedUserType !== userType) {
            console.log('[SecureRoute] Updating userType from', userType, 'to', detectedUserType);
            dispatch(setUserType(detectedUserType));
          } else {
            console.log('[SecureRoute] userType unchanged:', detectedUserType);
          }

          if (!userLogInStatus.hasRedirected || !userLogInStatus.isLoggedIn) {
            dispatch(setLogInStatus(LogInAction.EnableAll));
          }

          // Set user info for analytics
          if (user.email) {
            console.log('Set Matomo user id: ' + user.email);
            pushInstruction('setUserId', user.email);
          }
        })
        .catch((err: unknown) => {
          console.error('Error acquiring token:', err);
        });
    }

    if (!loaded) {
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, isAuthenticated, accounts, inProgress, dispatch, pushInstruction, loaded]);

  if (!isAuthenticated) {
    return (
      <Box sx={{pt: 10, width: '100%'}}>
        <LinearProgress color='secondary' />
      </Box>
    );
  }
  return <Outlet />;
};
